<template>
  <FlashNotifications ref="flash" />

  <!-- ── Drawer backdrop ── -->
  <Transition name="backdrop">
    <div v-if="drawerOpen"
      class="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
      @click="closeDrawer"/>
  </Transition>

  <!-- ── Slide-in drawer (right, wider) ── -->
  <Transition name="drawer">
    <div v-if="drawerOpen"
      class="fixed inset-y-0 right-0 z-50 w-full sm:w-[620px] bg-white dark:bg-[#1C2128] flex flex-col lg:flex-row shadow-2xl border-l border-gray-200 dark:border-gray-700 transition-[width] duration-300"
      :class="showPreview ? 'lg:w-[1500px]' : 'lg:w-[620px]'">

      <QuestionPreview
        v-if="showPreview"
        :question-type="form.question_type"
        :title="form.title"
        :question-details="form.question_details" />

      <!-- ── RIGHT: Editor panel ── -->
      <div class="flex-1 flex flex-col overflow-hidden min-w-0">

      <!-- Drawer header -->
      <div class="flex items-center justify-between px-7 py-5 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
        <div>
          <h2 class="text-xl font-bold text-gray-900 dark:text-white">
            {{ form.id ? 'Edit Question' : 'New Question' }}
          </h2>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            {{ form.id ? 'Update the question details below.' : 'Add a custom field to your booking form.' }}
          </p>
        </div>
        <div class="flex items-center gap-2">
          <!-- Preview toggle (lg screens only) -->
          <button @click="showPreview = !showPreview" type="button"
            class="hidden lg:inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all duration-150"
            :class="showPreview
              ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-400'
              : 'bg-white dark:bg-transparent border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600 hover:text-gray-700 dark:hover:text-gray-300'">
            <svg v-if="showPreview" class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"/>
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/>
            </svg>
            <svg v-else class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"/>
            </svg>
            Preview
            <span class="relative inline-flex h-3.5 w-6 flex-shrink-0 items-center rounded-full transition-colors duration-200"
              :class="showPreview ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'">
              <span class="inline-block h-2.5 w-2.5 transform rounded-full bg-white shadow-sm transition-transform duration-200"
                :class="showPreview ? 'translate-x-3' : 'translate-x-0.5'"></span>
            </span>
          </button>
          <!-- Close -->
          <button @click="closeDrawer"
            class="w-9 h-9 flex items-center justify-center rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Drawer body (scrollable) -->
      <div class="flex-1 overflow-y-auto px-7 py-6 space-y-6">

        <!-- Title -->
        <div class="space-y-1.5">
          <div class="flex items-center justify-between">
            <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Question Title <span class="text-red-400">*</span>
            </label>
            <span class="text-[11px] text-gray-400 tabular-nums">{{ form.title.length }}/255</span>
          </div>
          <input v-model="form.title" ref="titleInput" type="text" maxlength="255"
            placeholder="e.g. What is your company name?"
            class="w-full px-4 py-3 text-sm border rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
            :class="formErrors.title ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-gray-700'"/>
          <p v-if="formErrors.title" class="text-xs text-red-500 dark:text-red-400">{{ formErrors.title }}</p>
        </div>

        <!-- Answer type -->
        <div class="space-y-2.5">
          <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Answer Type</label>
          <div class="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            <label v-for="type in QUESTION_TYPES" :key="type.value"
              class="flex items-center gap-3 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all select-none"
              :class="form.question_type === type.value
                ? 'border-blue-500 bg-blue-50/60 dark:bg-blue-900/10'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-transparent'">
              <input type="radio" v-model="form.question_type" :value="type.value" class="sr-only"/>
              <span class="flex-shrink-0 transition-colors"
                :class="form.question_type === type.value ? 'text-blue-600 dark:text-blue-400' : 'text-gray-300 dark:text-gray-600'"
                v-html="type.icon"/>
              <div class="min-w-0">
                <p class="text-sm font-semibold leading-tight transition-colors"
                  :class="form.question_type === type.value ? 'text-blue-700 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'">
                  {{ type.label }}
                </p>
                <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-tight">{{ type.hint }}</p>
              </div>
            </label>
          </div>
        </div>

        <!-- Placeholder (one_line, multi_line, phone_number, email, number, url) -->
        <Transition name="options-reveal">
          <div v-if="isPlaceholderType" class="space-y-1.5">
            <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Placeholder <span class="font-normal normal-case text-gray-400">(optional)</span>
            </label>
            <input v-model="form.question_details.placeholder" type="text" maxlength="255"
              :placeholder="typePlaceholder(form.question_type, '')"
              class="w-full px-4 py-3 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
            <p class="text-[11px] text-gray-400 dark:text-gray-500">Hint text shown inside the input. Leave blank to use the default.</p>
          </div>
        </Transition>

        <!-- Min / max length (one_line, multi_line only) -->
        <Transition name="options-reveal">
          <div v-if="isTextInputType" class="space-y-3">
            <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Length <span class="font-normal normal-case text-gray-400">(optional)</span>
            </label>
            <div class="grid grid-cols-2 gap-3">
              <div class="space-y-1">
                <label class="text-[11px] text-gray-400 dark:text-gray-500">Min characters</label>
                <input v-model="form.question_details.min_length" type="number" min="0" placeholder="e.g. 2"
                  class="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div class="space-y-1">
                <label class="text-[11px] text-gray-400 dark:text-gray-500">Max characters</label>
                <input v-model="form.question_details.max_length" type="number" min="1" placeholder="e.g. 200"
                  class="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
            </div>
          </div>
        </Transition>

        <!-- Options (for choice types) — redesigned with drag-to-reorder -->
        <Transition name="options-reveal">
          <div v-if="isChoiceType" class="space-y-2">
            <div class="flex items-center justify-between">
              <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Options <span class="text-red-400">*</span>
              </label>
              <span class="inline-flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500">
                <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 16 16">
                  <circle cx="5" cy="4" r="1.3"/><circle cx="11" cy="4" r="1.3"/>
                  <circle cx="5" cy="8" r="1.3"/><circle cx="11" cy="8" r="1.3"/>
                  <circle cx="5" cy="12" r="1.3"/><circle cx="11" cy="12" r="1.3"/>
                </svg>
                Drag to reorder
              </span>
            </div>

            <div class="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-[#1C2128]">
              <!-- Option rows -->
              <div class="divide-y divide-gray-100 dark:divide-gray-700/60">
                <div v-for="(option, idx) in form.question_details.options" :key="idx"
                  class="flex items-center gap-2 pl-2 pr-3 py-0.5 transition-all duration-150"
                  :class="{
                    'opacity-30': dragIndex === idx,
                    'bg-blue-50 dark:bg-blue-900/10 border-t-2 border-t-blue-400 dark:border-t-blue-500':
                      dragOverIndex === idx && dragOverIndex !== dragIndex,
                  }"
                  draggable="true"
                  @dragstart="onDragStart($event, idx)"
                  @dragover.prevent="onDragOver($event, idx)"
                  @dragleave="onDragLeave"
                  @drop.prevent="onDrop($event, idx)"
                  @dragend="onDragEnd">

                  <!-- Drag handle -->
                  <div class="cursor-grab active:cursor-grabbing p-2 text-gray-300 dark:text-gray-600 hover:text-gray-400 dark:hover:text-gray-400 transition-colors flex-shrink-0">
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
                      <circle cx="5" cy="4" r="1.4"/><circle cx="11" cy="4" r="1.4"/>
                      <circle cx="5" cy="8" r="1.4"/><circle cx="11" cy="8" r="1.4"/>
                      <circle cx="5" cy="12" r="1.4"/><circle cx="11" cy="12" r="1.4"/>
                    </svg>
                  </div>

                  <!-- Option number -->
                  <span class="w-5 text-center text-[11px] font-semibold text-gray-300 dark:text-gray-600 flex-shrink-0 tabular-nums">
                    {{ idx + 1 }}
                  </span>

                  <!-- Input -->
                  <input v-model="form.question_details.options[idx]"
                    :ref="el => { if (el) optionRefs[idx] = el }"
                    :placeholder="`Option ${idx + 1}`"
                    type="text"
                    class="flex-1 py-3 text-sm text-gray-900 dark:text-gray-200 placeholder-gray-300 dark:placeholder-gray-600 bg-transparent border-0 focus:outline-none focus:ring-0"/>

                  <!-- Empty indicator -->
                  <span v-if="form.question_details.options[idx].trim() === ''"
                    class="text-[10px] text-amber-400 dark:text-amber-500 font-medium flex-shrink-0">
                    required
                  </span>

                  <!-- Remove -->
                  <button v-if="form.question_details.options.length > 1"
                    @click="removeOption(idx)"
                    type="button"
                    class="w-7 h-7 flex items-center justify-center rounded-lg text-gray-300 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex-shrink-0">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
                    </svg>
                  </button>
                  <div v-else class="w-7 flex-shrink-0"></div>
                </div>
              </div>

              <!-- Add option -->
              <button @click="addOption" type="button"
                class="w-full flex items-center gap-2.5 px-4 py-3 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:bg-blue-50/60 dark:hover:bg-blue-900/10 transition-colors border-t border-gray-100 dark:border-gray-700">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
                </svg>
                Add option
              </button>
            </div>
            <p v-if="formErrors.options" class="text-xs text-red-500 dark:text-red-400">{{ formErrors.options }}</p>
          </div>
        </Transition>

        <!-- Number — optional min / max -->
        <Transition name="options-reveal">
          <div v-if="isNumberType" class="space-y-3">
            <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Constraints <span class="font-normal normal-case text-gray-400">(optional)</span></label>
            <div class="grid grid-cols-2 gap-3">
              <div class="space-y-1">
                <label class="text-[11px] text-gray-400 dark:text-gray-500">Min value</label>
                <input v-model="form.question_details.min" type="number" placeholder="e.g. 0"
                  class="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div class="space-y-1">
                <label class="text-[11px] text-gray-400 dark:text-gray-500">Max value</label>
                <input v-model="form.question_details.max" type="number" placeholder="e.g. 100"
                  class="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
            </div>
          </div>
        </Transition>

        <!-- Date — mode selector + optional constraints -->
        <Transition name="options-reveal">
          <div v-if="isDateType" class="space-y-4">

            <!-- Mode dropdown -->
            <div class="space-y-1.5">
              <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date format</label>
              <div class="relative">
                <select v-model="form.question_details.date_mode"
                  class="toolbar-select w-full px-4 py-3 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="date">Date</option>
                  <option value="date_range">Date range</option>
                  <option value="time">Time</option>
                  <option value="datetime">Date and time</option>
                </select>
              </div>
            </div>

            <!-- Date constraint — date / date_range modes only -->
            <Transition name="options-reveal">
              <div v-if="['date','date_range'].includes(form.question_details.date_mode || 'date')" class="space-y-2">
                <div class="flex items-center justify-between">
                  <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Allowed date range <span class="font-normal normal-case text-gray-400">(optional)</span>
                  </label>
                  <button v-if="form.question_details.min_date || form.question_details.max_date"
                    @click="form.question_details.min_date = ''; form.question_details.max_date = ''"
                    type="button"
                    class="text-[11px] text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors">
                    Clear
                  </button>
                </div>

                <!-- Desktop: v-calendar range picker -->
                <div class="hidden sm:block">
                  <v-date-picker
                    v-model="dateRangeModel"
                    mode="date"
                    is-range
                    :locale="CURRENT_LOCALE"
                    :is-dark="isDark"
                    :popover="{ visibility: 'click' }">
                    <template #default="{ inputValue, inputEvents }">
                      <div class="grid grid-cols-2 gap-3">
                        <div class="space-y-1">
                          <label class="text-[11px] text-gray-400 dark:text-gray-500">Earliest date</label>
                          <div class="relative">
                            <input :value="inputValue.start" v-on="inputEvents.start"
                              placeholder="No limit" readonly
                              class="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer" />
                            <svg class="w-4 h-4 text-gray-300 dark:text-gray-600 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
                            </svg>
                          </div>
                        </div>
                        <div class="space-y-1">
                          <label class="text-[11px] text-gray-400 dark:text-gray-500">Latest date</label>
                          <div class="relative">
                            <input :value="inputValue.end" v-on="inputEvents.end"
                              placeholder="No limit" readonly
                              class="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer" />
                            <svg class="w-4 h-4 text-gray-300 dark:text-gray-600 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
                            </svg>
                          </div>
                        </div>
                      </div>
                    </template>
                  </v-date-picker>
                </div>

                <!-- Mobile: native date inputs -->
                <div class="sm:hidden grid grid-cols-2 gap-3">
                  <div class="space-y-1">
                    <label class="text-[11px] text-gray-400 dark:text-gray-500">Earliest date</label>
                    <input v-model="form.question_details.min_date" type="date"
                      class="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                  <div class="space-y-1">
                    <label class="text-[11px] text-gray-400 dark:text-gray-500">Latest date</label>
                    <input v-model="form.question_details.max_date" type="date"
                      class="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                </div>
              </div>
            </Transition>

          </div>
        </Transition>

        <!-- Default value — all types except rating, file_upload, signature -->
        <Transition name="options-reveal">
          <div v-if="isDefaultValueType" class="space-y-1.5">
            <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Default value <span class="font-normal normal-case text-gray-400">(optional)</span>
            </label>

            <!-- Boolean: fixed Yes / No picker -->
            <select v-if="form.question_type === 'boolean'" v-model="form.question_details.default"
              class="toolbar-select w-full px-4 py-3 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option value="">No default</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>

            <!-- Choice types: pick from current options -->
            <select v-else-if="isChoiceType && form.question_details.options.some(o => o.trim())"
              v-model="form.question_details.default"
              class="toolbar-select w-full px-4 py-3 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option value="">No default</option>
              <option v-for="opt in form.question_details.options.filter(o => o.trim())" :key="opt" :value="opt">{{ opt }}</option>
            </select>

            <!-- Multi-line: textarea so the default value has room -->
            <textarea v-else-if="form.question_type === 'multi_line'"
              v-model="form.question_details.default" rows="3"
              placeholder="e.g. pre-filled answer"
              class="w-full px-4 py-3 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none leading-relaxed" />

            <!-- Date type: smart input matching the selected date_mode -->
            <template v-else-if="isDateType">
              <!-- date — single date picker -->
              <input v-if="!form.question_details.date_mode || form.question_details.date_mode === 'date'"
                v-model="form.question_details.default" type="date"
                class="w-full px-4 py-3 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />

              <!-- date_range — v-calendar range picker -->
              <v-date-picker v-else-if="form.question_details.date_mode === 'date_range'"
                v-model="defaultDateRangeModel"
                mode="date" is-range :is-dark="isDark" :popover="{ visibility: 'click' }">
                <template #default="{ inputValue, inputEvents }">
                  <div class="grid grid-cols-2 gap-3">
                    <div class="relative">
                      <svg class="w-4 h-4 text-gray-300 dark:text-gray-600 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
                      </svg>
                      <input :value="inputValue.start" v-on="inputEvents.start" placeholder="Start date" readonly
                        class="w-full pl-9 pr-3 py-3 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer" />
                    </div>
                    <div class="relative">
                      <svg class="w-4 h-4 text-gray-300 dark:text-gray-600 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
                      </svg>
                      <input :value="inputValue.end" v-on="inputEvents.end" placeholder="End date" readonly
                        class="w-full pl-9 pr-3 py-3 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 placeholder-gray-300 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer" />
                    </div>
                  </div>
                </template>
              </v-date-picker>

              <!-- time — dropdown with 30-minute slots -->
              <select v-else-if="form.question_details.date_mode === 'time'"
                v-model="form.question_details.default"
                class="toolbar-select w-full px-4 py-3 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option value="">No default</option>
                <option v-for="opt in TIME_OPTIONS" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>

              <!-- datetime — datetime-local picker -->
              <input v-else-if="form.question_details.date_mode === 'datetime'"
                v-model="form.question_details.default" type="datetime-local"
                class="w-full px-4 py-3 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
            </template>

            <!-- All other types: text input -->
            <input v-else v-model="form.question_details.default" type="text"
              :placeholder="isHiddenType ? 'e.g. utm_source value' : 'e.g. pre-filled answer'"
              class="w-full px-4 py-3 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />

            <p v-if="isHiddenType" class="text-[11px] text-gray-400 dark:text-gray-500 leading-relaxed">
              Never shown to the booker. Stored with every booking. Override per-link by appending
              <code class="bg-gray-100 dark:bg-gray-800 px-1 rounded">?field_name=value</code> to your booking URL.
            </p>
            <p v-else class="text-[11px] text-gray-400 dark:text-gray-500">
              Pre-fills the field when the booking form loads. The booker can still change it before submitting.
            </p>
          </div>
        </Transition>

        <!-- Rating — scale selector + live preview -->
        <Transition name="options-reveal">
          <div v-if="isRatingType" class="space-y-3">
            <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Scale</label>
            <div class="grid grid-cols-2 gap-2.5">
              <label v-for="opt in [{ value: '5', label: '★ 5 Stars', sub: 'Classic star rating' }, { value: '10', label: '0 – 10  NPS', sub: 'Net Promoter Score' }]"
                :key="opt.value"
                class="flex items-start gap-3 p-3.5 rounded-xl border-2 cursor-pointer transition-all select-none"
                :class="form.question_details.scale === opt.value
                  ? 'border-blue-500 bg-blue-50/60 dark:bg-blue-900/10'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'">
                <input type="radio" v-model="form.question_details.scale" :value="opt.value" class="sr-only" />
                <div>
                  <p class="text-sm font-semibold leading-tight"
                    :class="form.question_details.scale === opt.value ? 'text-blue-700 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'">
                    {{ opt.label }}
                  </p>
                  <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{{ opt.sub }}</p>
                </div>
              </label>
            </div>
            <!-- Live preview -->
            <div class="rounded-xl border border-gray-100 dark:border-gray-700/60 bg-gray-50/60 dark:bg-gray-800/30 px-4 py-3">
              <p class="text-[11px] text-gray-400 dark:text-gray-500 mb-2 uppercase tracking-wider font-semibold">Preview</p>
              <div v-if="form.question_details.scale === '5'" class="flex gap-1.5">
                <svg v-for="i in 5" :key="i" class="w-7 h-7 text-yellow-400" fill="currentColor" viewBox="0 0 576 512">
                  <path d="M316.9 18C311.6 7 300.4 0 288.1 0s-23.4 7-28.8 18L195 150.3 51.4 171.5c-12 1.8-22 10.2-25.7 21.7s-.7 24.2 7.9 32.7L137.8 329 113.2 474.7c-2 12 3 24.2 12.9 31.3s23 8 33.8 2.3l128.3-68.5 128.3 68.5c10.8 5.7 23.9 4.9 33.8-2.3s14.9-19.3 12.9-31.3L438.5 329 542.7 225.9c8.6-8.5 11.7-21.2 7.9-32.7s-13.7-19.9-25.7-21.7L381.2 150.3 316.9 18z"/>
                </svg>
              </div>
              <div v-else class="flex flex-wrap gap-1.5">
                <span v-for="i in 11" :key="i"
                  class="w-8 h-8 flex items-center justify-center rounded-lg text-sm font-semibold border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400">
                  {{ i - 1 }}
                </span>
              </div>
            </div>
          </div>
        </Transition>

        <!-- File upload — allowed types only (size hardcoded to 5 MB) -->
        <Transition name="options-reveal">
          <div v-if="isFileUploadType" class="space-y-2">
            <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Accepted file types <span class="font-normal normal-case text-gray-400">(leave empty to accept all)</span>
            </label>
            <div class="rounded-xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700/60 overflow-hidden">
              <div v-for="group in FILE_TYPE_GROUPS" :key="group.label" class="px-4 py-3">
                <p class="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">{{ group.label }}</p>
                <div class="flex flex-wrap gap-2">
                  <label v-for="ft in group.types" :key="ft.value"
                    class="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium cursor-pointer transition-colors select-none"
                    :class="form.question_details.allowed_types.includes(ft.value)
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                      : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-gray-300'">
                    <input type="checkbox" :value="ft.value" v-model="form.question_details.allowed_types" class="sr-only" />
                    .{{ ft.value }}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </Transition>

        <!-- Signature — declaration text -->
        <Transition name="options-reveal">
          <div v-if="isSignatureType" class="space-y-1.5">
            <label class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Declaration text <span class="font-normal normal-case text-gray-400">(optional)</span>
            </label>
            <textarea v-model="form.question_details.declaration_text" rows="3" maxlength="1000"
              placeholder="e.g. By signing below, I agree to the terms and conditions of this booking."
              class="w-full px-4 py-3 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none leading-relaxed" />
            <div class="flex items-center justify-between">
              <p class="text-[11px] text-gray-400 dark:text-gray-500">Shown to the booker above the signature field.</p>
              <span class="text-[11px] text-gray-400 tabular-nums">{{ form.question_details.declaration_text.length }}/1000</span>
            </div>
          </div>
        </Transition>

        <!-- State toggle -->
        <div class="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div class="flex items-center gap-3 px-4 py-4 bg-white dark:bg-[#1C2128]">
            <div class="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-semibold text-gray-900 dark:text-white">Active</p>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Show this question on booking forms</p>
            </div>
            <button type="button"
              @click="form.state = form.state === 'active' ? 'draft' : 'active'"
              :class="form.state === 'active' ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'"
              class="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none">
              <span :class="form.state === 'active' ? 'translate-x-5' : 'translate-x-0.5'"
                class="inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200"/>
            </button>
          </div>
        </div>

      </div>

      <!-- Drawer footer -->
      <div class="flex-shrink-0 flex gap-3 px-7 py-5 border-t border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/20">
        <button @click="closeDrawer" type="button"
          class="flex-1 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl transition-colors">
          Cancel
        </button>
        <button @click="submitForm" type="button" :disabled="saving"
          class="flex-1 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-xl transition-colors flex items-center justify-center gap-2">
          <svg v-if="saving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
          </svg>
          {{ saving ? 'Saving...' : (form.id ? 'Update Question' : 'Create Question') }}
        </button>
      </div>

      </div><!-- end editor panel -->
    </div>
  </Transition>

  <!-- ── Delete confirmation modal ── -->
  <Transition name="modal">
    <div v-if="showDeleteModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      @click.self="showDeleteModal = false">
      <div class="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
      <div class="relative bg-white dark:bg-[#1C2128] rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 max-w-sm w-full p-6 modal-card">
        <div class="w-12 h-12 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-4">
          <svg class="w-6 h-6 text-red-500" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
          </svg>
        </div>
        <h3 class="text-lg font-bold text-gray-900 dark:text-white text-center mb-2">Delete question?</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
          This question will be permanently removed from all booking forms.
        </p>
        <div class="flex gap-3">
          <button @click="showDeleteModal = false"
            class="flex-1 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
            Cancel
          </button>
          <button @click="deleteQuestion" :disabled="deleting"
            class="flex-1 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 rounded-lg transition-colors flex items-center justify-center gap-2">
            <svg v-if="deleting" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
            {{ deleting ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>
  </Transition>

  <!-- ── Events modal ── -->
  <Transition name="backdrop">
    <div v-if="eventsModal.open"
      class="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4"
      @click.self="closeEventsModal">
      <Transition name="modal-pop">
        <div v-if="eventsModal.open"
          class="relative w-full max-w-md bg-white dark:bg-[#1C2128] rounded-2xl shadow-2xl overflow-hidden"
          @click.stop>

          <!-- Header -->
          <div class="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100 dark:border-gray-700">
            <div class="min-w-0 pr-4">
              <h3 class="text-base font-bold text-gray-900 dark:text-white">Used in events</h3>
              <p class="text-sm text-gray-400 dark:text-gray-500 mt-0.5 truncate">{{ eventsModal.questionTitle }}</p>
            </div>
            <button @click="closeEventsModal"
              class="shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- Body -->
          <div class="max-h-96 overflow-y-auto">

            <!-- Loading -->
            <div v-if="eventsModal.loading" class="px-6 py-4 space-y-3 animate-pulse">
              <div v-for="i in eventsModal.count" :key="i" class="flex items-center gap-3">
                <div class="w-3 h-3 rounded-full bg-gray-200 dark:bg-gray-700 shrink-0"></div>
                <div class="flex-1 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div class="w-14 h-6 bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
              </div>
            </div>

            <!-- Error -->
            <div v-else-if="eventsModal.error"
              class="flex flex-col items-center justify-center py-12 text-center px-6">
              <p class="text-sm font-medium text-red-500 dark:text-red-400">Failed to load events. Please try again.</p>
            </div>

            <!-- Empty -->
            <div v-else-if="eventsModal.events.length === 0"
              class="flex flex-col items-center justify-center py-12 text-center px-6">
              <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Not used in any events yet.</p>
            </div>

            <!-- Event list -->
            <div v-else class="divide-y divide-gray-100 dark:divide-gray-700/60">
              <div v-for="event in eventsModal.events" :key="event.id"
                class="flex items-center gap-3 px-6 py-3.5">
                <div class="w-3 h-3 rounded-full shrink-0" :class="event.color"></div>
                <span class="flex-1 min-w-0 text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                  {{ event.name }}
                </span>
                <span class="shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                  :class="event.active ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500'">
                  {{ event.active ? 'Active' : 'Inactive' }}
                </span>
                <router-link :to="`/events/${event.id}/edit`" data-turbo-prefetch="false"
                  @click="closeEventsModal"
                  class="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Z"/>
                  </svg>
                  Edit
                </router-link>
              </div>
            </div>

          </div>

          <!-- Footer -->
          <div class="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex justify-end">
            <button @click="closeEventsModal"
              class="px-4 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
              Close
            </button>
          </div>

        </div>
      </Transition>
    </div>
  </Transition>

  <!-- ── Page ── -->
  <div class="min-h-app-screen bg-gray-50 dark:bg-[#0d1117]">

    <!-- ══ MOBILE ══ -->
    <div class="sm:hidden">
      <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700">
        <div class="flex items-center justify-between px-4 h-14">
          <div class="flex items-center gap-2">
            <h1 class="text-[22px] font-bold text-gray-900 dark:text-white tracking-tight leading-none">Form Questions</h1>
            <span v-if="!store.loading && store.questions.length > 0"
              class="px-2 py-0.5 rounded-full text-xs font-bold bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 tabular-nums">
              {{ store.questions.length }}
            </span>
          </div>
          <button v-if="!store.loading && !store.error" @click="openCreate"
            class="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center shadow-lg shadow-blue-600/25 active:scale-95 transition-transform">
            <svg class="w-[18px] h-[18px] text-white" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
            </svg>
          </button>
        </div>
      </div>

      <div class="px-4 pt-4 pb-28 space-y-3">
        <template v-if="store.loading">
          <div v-for="i in 4" :key="i" class="bg-white dark:bg-[#1C2128] rounded-2xl border border-gray-100 dark:border-gray-700 p-4 animate-pulse">
            <div class="flex items-start gap-3">
              <div class="h-6 w-20 bg-gray-200 dark:bg-gray-700 rounded-full flex-shrink-0"></div>
              <div class="flex-1 space-y-2">
                <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-1/2"></div>
              </div>
              <div class="h-6 w-14 bg-gray-100 dark:bg-gray-800 rounded-full flex-shrink-0"></div>
            </div>
          </div>
        </template>

        <template v-else-if="store.error">
          <div class="flex flex-col items-center justify-center py-20 text-center">
            <div class="w-14 h-14 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
              <svg class="w-7 h-7 text-red-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
              </svg>
            </div>
            <p class="font-semibold text-gray-900 dark:text-white mb-1">Something went wrong</p>
            <p class="text-sm text-gray-400 dark:text-gray-500 mb-4">{{ store.error }}</p>
            <button @click="retry" class="text-blue-600 dark:text-blue-400 font-semibold text-sm">Try again</button>
          </div>
        </template>

        <template v-else-if="store.questions.length === 0">
          <div class="flex flex-col items-center justify-center pt-24 pb-16 text-center">
            <div class="w-20 h-20 rounded-3xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-5">
              <svg class="w-10 h-10 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z"/>
              </svg>
            </div>
            <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-2">No questions yet</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 max-w-[240px] mb-6">Add custom questions to collect information from clients at booking time.</p>
            <button @click="openCreate" class="px-6 py-3 bg-blue-600 text-white font-semibold rounded-2xl text-sm shadow-md shadow-blue-600/30">
              Add first question
            </button>
          </div>
        </template>

        <template v-else>
          <!-- ── Mobile toolbar: search + filter button ── -->
          <div class="flex items-center gap-2">
            <div class="relative flex-1">
              <svg class="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
              </svg>
              <input v-model="searchQuery" type="text" placeholder="Search questions…"
                class="w-full pl-10 pr-9 py-3 text-sm bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              <button v-if="searchQuery" @click="searchQuery = ''"
                class="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <!-- Filter button -->
            <button @click="filterSheetOpen = true" type="button"
              class="relative flex items-center gap-1.5 px-3.5 py-3 rounded-2xl border text-sm font-semibold transition-colors flex-shrink-0 active:scale-95"
              :class="activeFilterCount > 0
                ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-600/25'
                : 'bg-white dark:bg-[#1C2128] border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75"/>
              </svg>
              Filters
              <span v-if="activeFilterCount > 0"
                class="w-4 h-4 rounded-full bg-white text-blue-600 text-[10px] font-bold flex items-center justify-center leading-none">
                {{ activeFilterCount }}
              </span>
            </button>
          </div>

          <!-- Results meta -->
          <div class="flex items-center justify-between px-0.5">
            <p class="text-xs text-gray-500 dark:text-gray-400">
              <span class="font-semibold text-gray-700 dark:text-gray-300">{{ filteredQuestions.length }}</span>
              of
              <span class="font-semibold text-gray-700 dark:text-gray-300">{{ store.questions.length }}</span>
              question{{ store.questions.length === 1 ? '' : 's' }}
            </p>
            <button v-if="hasActiveFilters" @click="clearFilters"
              class="text-xs font-semibold text-blue-600 dark:text-blue-400 flex items-center gap-1">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
              Clear all
            </button>
          </div>

          <!-- No results after filtering -->
          <div v-if="filteredQuestions.length === 0" class="flex flex-col items-center justify-center pt-10 pb-4 text-center">
            <div class="w-14 h-14 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
              <svg class="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
              </svg>
            </div>
            <p class="font-semibold text-gray-900 dark:text-white mb-1">No questions match</p>
            <p class="text-sm text-gray-400 dark:text-gray-500 mb-4">Try adjusting your search or filters.</p>
            <button @click="clearFilters" class="text-sm text-blue-600 dark:text-blue-400 font-semibold">Clear filters</button>
          </div>

          <div v-for="question in filteredQuestions" :key="question.id"
            class="bg-white dark:bg-[#1C2128] rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div class="p-4">
              <div class="flex items-start gap-3">
                <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold flex-shrink-0"
                  :class="typeColor(question.question_type)">
                  <span v-html="typeIconSm(question.question_type)"></span>
                  {{ typeLabel(question.question_type) }}
                </span>
                <div class="flex-1 min-w-0">
                  <p class="font-semibold text-sm text-gray-900 dark:text-white leading-snug">{{ question.title }}</p>
                  <div v-if="hasOptions(question)" class="flex flex-wrap gap-1 mt-1.5">
                    <span v-for="opt in visibleOptions(question)" :key="opt"
                      class="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-[10px] rounded-full">{{ opt }}</span>
                    <span v-if="hiddenOptionCount(question) > 0"
                      class="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 text-[10px] rounded-full">
                      +{{ hiddenOptionCount(question) }} more
                    </span>
                  </div>
                </div>
                <div class="flex items-center gap-1.5 flex-shrink-0">
                  <button v-if="question.events_count > 0"
                    type="button"
                    @click="openEventsModal(question)"
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-400 hover:bg-violet-100 dark:hover:bg-violet-900/40 transition-colors">
                    <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
                    </svg>
                    {{ question.events_count }}
                  </button>
                  <span class="px-2 py-0.5 rounded-full text-[10px] font-semibold"
                    :class="question.state === 'active' ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500'">
                    {{ question.state === 'active' ? 'Active' : 'Draft' }}
                  </span>
                </div>
              </div>
            </div>
            <div class="flex items-center gap-1 px-4 pb-3 border-t border-gray-50 dark:border-gray-800 pt-2.5">
              <div class="flex-1"></div>
              <button @click="openEdit(question)"
                class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125"/>
                </svg>
                Edit
              </button>
              <button @click="openDelete(question.id)"
                class="p-2 rounded-lg text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
                </svg>
              </button>
            </div>
          </div>

          <div class="rounded-xl border border-blue-100 dark:border-blue-900/40 bg-blue-50/50 dark:bg-blue-950/20 px-4 py-3.5 flex gap-3">
            <svg class="w-4 h-4 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"/>
            </svg>
            <div>
              <p class="text-sm font-semibold text-blue-800 dark:text-blue-300">Questions appear on your event booking forms</p>
              <p class="text-xs text-blue-600/80 dark:text-blue-400/80 mt-0.5 leading-relaxed">Assign questions to individual events from the event editor. Only active questions can be assigned.</p>
            </div>
          </div>
        </template>
      </div>

      <!-- ── Filter bottom sheet backdrop ── -->
      <Transition name="backdrop">
        <div v-if="filterSheetOpen"
          class="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
          @click="filterSheetOpen = false"/>
      </Transition>

      <!-- ── Filter bottom sheet ── -->
      <Transition name="sheet">
        <div v-if="filterSheetOpen"
          class="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-[#1C2128] rounded-t-3xl shadow-2xl">

          <!-- Drag handle -->
          <div class="flex justify-center pt-3 pb-1">
            <div class="w-10 h-1 rounded-full bg-gray-200 dark:bg-gray-700"></div>
          </div>

          <!-- Sheet header -->
          <div class="flex items-center justify-between px-5 pt-2 pb-4 border-b border-gray-100 dark:border-gray-700">
            <div>
              <h3 class="text-base font-bold text-gray-900 dark:text-white">Filters &amp; Sort</h3>
              <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Refine your question list</p>
            </div>
            <button @click="filterSheetOpen = false" type="button"
              class="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 active:scale-90 transition-transform">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- Sheet body -->
          <div class="overflow-y-auto px-5 py-5 space-y-6" style="max-height:62vh">

            <!-- Type -->
            <div>
              <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">Question type</p>
              <div class="flex flex-wrap gap-2">
                <button @click="filterType = ''" type="button"
                  class="px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors"
                  :class="filterType === ''
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 bg-white dark:bg-transparent'">
                  All
                </button>
                <button v-for="t in QUESTION_TYPES" :key="t.value" @click="filterType = t.value" type="button"
                  class="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors"
                  :class="filterType === t.value
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 bg-white dark:bg-transparent'">
                  <span v-html="t.icon" class="scale-75 flex-shrink-0 leading-none"></span>
                  {{ t.label }}
                </button>
              </div>
            </div>

            <!-- Status -->
            <div>
              <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">Status</p>
              <div class="flex gap-2">
                <button v-for="opt in [{ value: '', label: 'All' }, { value: 'active', label: 'Active' }, { value: 'draft', label: 'Draft' }]"
                  :key="opt.value"
                  @click="filterStatus = opt.value" type="button"
                  class="flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-colors"
                  :class="filterStatus === opt.value
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 bg-white dark:bg-transparent'">
                  {{ opt.label }}
                </button>
              </div>
            </div>

            <!-- Sort -->
            <div>
              <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">Sort by</p>
              <div class="space-y-1.5">
                <button v-for="opt in SORT_OPTIONS" :key="opt.value"
                  @click="sortBy = opt.value" type="button"
                  class="w-full flex items-center justify-between px-4 py-3.5 rounded-2xl border text-sm transition-colors"
                  :class="sortBy === opt.value
                    ? 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 font-semibold'
                    : 'border-gray-100 dark:border-gray-700/60 text-gray-700 dark:text-gray-300 bg-white dark:bg-transparent'">
                  <span>{{ opt.label }}</span>
                  <svg v-if="sortBy === opt.value" class="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/>
                  </svg>
                </button>
              </div>
            </div>

          </div>

          <!-- Sheet footer -->
          <div class="flex gap-3 px-5 py-4 border-t border-gray-100 dark:border-gray-700">
            <button @click="clearFilters; sortBy = 'title_asc'" type="button"
              class="flex-1 py-3.5 rounded-2xl text-sm font-semibold border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 bg-white dark:bg-transparent active:scale-95 transition-transform">
              Clear all
            </button>
            <button @click="filterSheetOpen = false" type="button"
              class="flex-[2] py-3.5 rounded-2xl text-sm font-semibold bg-blue-600 text-white shadow-lg shadow-blue-600/20 active:scale-95 transition-transform">
              Done
            </button>
          </div>

        </div>
      </Transition>

    </div>


    <!-- ══ DESKTOP ══ -->
    <div class="hidden sm:flex sm:flex-col sm:h-app-screen">
      <div class="flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
        <div class="flex items-center gap-4 px-6 py-3.5">
          <div class="flex-shrink-0">
            <div class="flex items-center gap-2.5">
              <h1 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">Form Questions</h1>
              <span v-if="!store.loading && store.questions.length > 0"
                class="px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 tabular-nums">
                {{ store.questions.length }}
              </span>
            </div>
            <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">Custom fields shown to clients during the booking process.</p>
          </div>

          <div class="ml-auto flex-shrink-0">
            <button v-if="!store.loading && !store.error" @click="openCreate"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
              </svg>
              New Question
            </button>
          </div>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto px-6 py-6">
        <div class="max-w-4xl mx-auto">

          <template v-if="store.loading">
            <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700/80 overflow-hidden animate-pulse">
              <div class="px-6 py-3 bg-gray-50 dark:bg-gray-800/40">
                <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
              </div>
              <div v-for="i in 4" :key="i" class="flex items-center gap-4 px-6 py-4">
                <div class="h-6 w-24 bg-gray-200 dark:bg-gray-700 rounded-full flex-shrink-0"></div>
                <div class="flex-1 space-y-1.5">
                  <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                  <div class="flex gap-1.5">
                    <div class="h-4 w-16 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
                    <div class="h-4 w-20 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
                  </div>
                </div>
                <div class="h-6 w-14 bg-gray-100 dark:bg-gray-800 rounded-full flex-shrink-0"></div>
                <div class="flex gap-2">
                  <div class="h-8 w-16 bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
                  <div class="w-8 h-8 bg-gray-100 dark:bg-gray-800 rounded-lg"></div>
                </div>
              </div>
            </div>
          </template>

          <template v-else-if="store.error">
            <div class="flex flex-col items-center justify-center py-32 text-center">
              <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
                <svg class="w-8 h-8 text-red-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
                </svg>
              </div>
              <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">Something went wrong</h3>
              <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ store.error }}</p>
              <button @click="retry" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors">Try again</button>
            </div>
          </template>

          <template v-else-if="store.questions.length === 0">
            <div class="flex flex-col items-center justify-center py-32 text-center">
              <div class="w-24 h-24 rounded-3xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-6">
                <svg class="w-12 h-12 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z"/>
                </svg>
              </div>
              <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-2">No questions yet</h2>
              <p class="text-sm text-gray-500 dark:text-gray-400 max-w-sm mb-8">Add custom questions to collect information from clients during the booking process.</p>
              <button @click="openCreate"
                class="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors shadow-sm text-sm">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
                </svg>
                Add your first question
              </button>
            </div>
          </template>

          <template v-else>
            <!-- ── Desktop toolbar: search + type filter + status filter + sort ── -->
            <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 flex items-center gap-2 px-3 py-2.5 mb-3">
              <!-- Search -->
              <div class="relative flex-1 min-w-0">
                <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
                </svg>
                <input v-model="searchQuery" type="text" placeholder="Search questions…"
                  class="w-full pl-9 pr-8 py-2 text-sm bg-gray-50 dark:bg-gray-800/60 text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" />
                <button v-if="searchQuery" @click="searchQuery = ''"
                  class="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
                </button>
              </div>

              <div class="h-5 w-px bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>

              <!-- Type filter -->
              <select v-model="filterType" class="toolbar-select flex-shrink-0 pl-3 pr-7 py-2 text-sm border rounded-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                :class="filterType
                  ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 font-medium'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent text-gray-700 dark:text-gray-300'">
                <option value="">All types</option>
                <option v-for="t in QUESTION_TYPES" :key="t.value" :value="t.value">{{ t.label }}</option>
              </select>

              <!-- Status filter -->
              <select v-model="filterStatus" class="toolbar-select flex-shrink-0 pl-3 pr-7 py-2 text-sm border rounded-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                :class="filterStatus
                  ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 font-medium'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent text-gray-700 dark:text-gray-300'">
                <option value="">All status</option>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
              </select>

              <div class="h-5 w-px bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>

              <!-- Sort -->
              <div class="relative flex-shrink-0">
                <svg class="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3 4.5h14.25M3 9h9.75M3 13.5h9.75m4.5-4.5v12m0 0-3.75-3.75M17.25 21 21 17.25"/>
                </svg>
                <select v-model="sortBy" class="toolbar-select pl-9 pr-7 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent text-gray-700 dark:text-gray-300 rounded-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors">
                  <option value="title_asc">Title A → Z</option>
                  <option value="title_desc">Title Z → A</option>
                  <option value="type">By type</option>
                  <option value="status_active">Active first</option>
                  <option value="status_draft">Draft first</option>
                </select>
              </div>
            </div>

            <!-- Results meta -->
            <div class="flex items-center justify-between mb-2.5 px-0.5">
              <p class="text-xs text-gray-500 dark:text-gray-400">
                <span class="font-semibold text-gray-700 dark:text-gray-300">{{ filteredQuestions.length }}</span>
                of
                <span class="font-semibold text-gray-700 dark:text-gray-300">{{ store.questions.length }}</span>
                question{{ store.questions.length === 1 ? '' : 's' }}
              </p>
              <button v-if="hasActiveFilters" @click="clearFilters"
                class="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
                Clear filters
              </button>
            </div>

            <!-- No results after filtering -->
            <div v-if="filteredQuestions.length === 0"
              class="flex flex-col items-center justify-center py-20 text-center">
              <div class="w-14 h-14 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
                <svg class="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
                </svg>
              </div>
              <p class="font-semibold text-gray-900 dark:text-white mb-1">No questions match</p>
              <p class="text-sm text-gray-400 dark:text-gray-500 mb-4">Try adjusting your search or filters.</p>
              <button @click="clearFilters" class="text-sm text-blue-600 dark:text-blue-400 font-semibold hover:underline">Clear filters</button>
            </div>

            <div v-else class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700/80 overflow-hidden">
              <div class="grid grid-cols-[auto_1fr_auto_auto_auto] gap-4 items-center px-6 py-3 bg-gray-50 dark:bg-gray-800/40 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <span class="w-28">Type</span>
                <span>Question</span>
                <span class="w-20 text-center">Events</span>
                <span class="w-16 text-center">Status</span>
                <span class="w-28 text-right">Actions</span>
              </div>

              <div v-for="question in filteredQuestions" :key="question.id"
                class="group grid grid-cols-[auto_1fr_auto_auto_auto] gap-4 items-center px-6 py-4 hover:bg-gray-50/60 dark:hover:bg-gray-800/30 transition-colors">
                <div class="w-28">
                  <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
                    :class="typeColor(question.question_type)">
                    <span v-html="typeIconSm(question.question_type)"></span>
                    {{ typeLabel(question.question_type) }}
                  </span>
                </div>
                <div class="min-w-0">
                  <p class="font-semibold text-sm text-gray-900 dark:text-white leading-tight">{{ question.title }}</p>
                  <div v-if="hasOptions(question)" class="flex flex-wrap gap-1 mt-1">
                    <span v-for="opt in visibleOptions(question)" :key="opt"
                      class="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-[10px] rounded-full">{{ opt }}</span>
                    <span v-if="hiddenOptionCount(question) > 0"
                      class="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 text-[10px] rounded-full">
                      +{{ hiddenOptionCount(question) }} more
                    </span>
                  </div>
                </div>
                <div class="w-20 flex justify-center">
                  <button v-if="question.events_count > 0"
                    type="button"
                    @click="openEventsModal(question)"
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-400 hover:bg-violet-100 dark:hover:bg-violet-900/40 transition-colors cursor-pointer">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
                    </svg>
                    {{ question.events_count }}
                  </button>
                  <span v-else class="text-xs text-gray-300 dark:text-gray-600">—</span>
                </div>
                <div class="w-16 flex justify-center">
                  <span class="px-2.5 py-0.5 rounded-full text-[11px] font-semibold"
                    :class="question.state === 'active' ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500'">
                    {{ question.state === 'active' ? 'Active' : 'Draft' }}
                  </span>
                </div>
                <div class="w-28 flex items-center justify-end gap-1">
                  <button @click="openEdit(question)"
                    class="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125"/>
                    </svg>
                    Edit
                  </button>
                  <button @click="openDelete(question.id)"
                    class="p-1.5 rounded-lg text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <div class="mt-4 rounded-xl border border-blue-100 dark:border-blue-900/40 bg-blue-50/50 dark:bg-blue-950/20 px-5 py-4 flex gap-3.5">
              <svg class="w-5 h-5 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"/>
              </svg>
              <div>
                <p class="text-sm font-semibold text-blue-800 dark:text-blue-300">Questions appear on your event booking forms</p>
                <p class="text-xs text-blue-600/80 dark:text-blue-400/80 mt-0.5 leading-relaxed">
                  Assign questions to individual events from the event editor. Only active questions can be assigned.
                </p>
              </div>
            </div>
          </template>

        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onBeforeUpdate, onUnmounted, nextTick } from 'vue'
import { useQuestionsStore } from 'stores/questions.js'
import { useRequest } from 'composables/useRequest.js'
import { useConstants } from 'composables/useConstants.js'
import { useQuestionTypes } from 'composables/useQuestionTypes.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import QuestionPreview from './QuestionPreview.vue'

const store = useQuestionsStore()
const flash = ref(null)
const { axiosRequest } = useRequest(flash)
const { CURRENT_LOCALE } = useConstants()
const isDark = document.documentElement.classList.contains('dark')

const { QUESTION_TYPES, CHOICE_TYPES, typeLabel, typeColor, typeIconSm, typePlaceholder } = useQuestionTypes()

const TIME_OPTIONS = (() => {
  const opts = []
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hh = String(h).padStart(2, '0')
      const mm = String(m).padStart(2, '0')
      const val = `${hh}:${mm}`
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
      const period = h < 12 ? 'AM' : 'PM'
      opts.push({ value: val, label: `${h12}:${mm} ${period}` })
    }
  }
  return opts
})()

const FILE_TYPE_GROUPS = [
  { label: 'Documents', types: [{ value: 'pdf' }, { value: 'doc' }, { value: 'docx' }, { value: 'csv' }, { value: 'txt' }] },
  { label: 'Images',    types: [{ value: 'jpg' }, { value: 'jpeg' }, { value: 'png' }, { value: 'gif' }, { value: 'webp' }] },
]

function hasOptions(q) { return CHOICE_TYPES.includes(q.question_type) && q.question_details?.options?.some(o => o.trim()) }
function visibleOptions(q) { return (q.question_details?.options ?? []).filter(o => o.trim()).slice(0, 3) }
function hiddenOptionCount(q) { return Math.max(0, (q.question_details?.options ?? []).filter(o => o.trim()).length - 3) }

// ── Events modal ──
const eventsModal = ref({ open: false, questionTitle: '', events: [], loading: false, error: false })

async function openEventsModal(question) {
  eventsModal.value = { open: true, questionTitle: question.title, events: [], loading: true, error: false, count: question.events_count }
  const res = await axiosRequest('GET', `/api/v1/host/questions/${question.id}/events`, {}, false)
  eventsModal.value.loading = false
  if (!res.js_error && res.response.success) {
    eventsModal.value.events = res.response.data
  } else {
    eventsModal.value.error = true
  }
}

function closeEventsModal() {
  eventsModal.value.open = false
}

// ── Search / filter / sort ──
const searchQuery  = ref('')
const filterType   = ref('')
const filterStatus = ref('')
const sortBy       = ref('title_asc')

const hasActiveFilters = computed(() =>
  searchQuery.value.trim() !== '' || filterType.value !== '' || filterStatus.value !== ''
)

const activeFilterCount = computed(() =>
  (filterType.value ? 1 : 0) + (filterStatus.value ? 1 : 0)
)

const SORT_OPTIONS = [
  { value: 'title_asc',     label: 'Title A → Z' },
  { value: 'title_desc',    label: 'Title Z → A' },
  { value: 'type',          label: 'By type' },
  { value: 'status_active', label: 'Active first' },
  { value: 'status_draft',  label: 'Draft first' },
]

function clearFilters() {
  searchQuery.value  = ''
  filterType.value   = ''
  filterStatus.value = ''
}

const filteredQuestions = computed(() => {
  let list = [...store.questions]

  const q = searchQuery.value.trim().toLowerCase()
  if (q) {
    list = list.filter(question =>
      question.title.toLowerCase().includes(q) ||
      typeLabel(question.question_type).toLowerCase().includes(q)
    )
  }

  if (filterType.value)   list = list.filter(question => question.question_type === filterType.value)
  if (filterStatus.value) list = list.filter(question => question.state === filterStatus.value)

  switch (sortBy.value) {
    case 'title_desc':
      list.sort((a, b) => b.title.toLowerCase().localeCompare(a.title.toLowerCase()))
      break
    case 'type':
      list.sort((a, b) => typeLabel(a.question_type).localeCompare(typeLabel(b.question_type)))
      break
    case 'status_active':
      list.sort((a, b) => (a.state === b.state ? 0 : a.state === 'active' ? -1 : 1))
      break
    case 'status_draft':
      list.sort((a, b) => (a.state === b.state ? 0 : a.state === 'draft' ? -1 : 1))
      break
    default: // title_asc
      list.sort((a, b) => a.title.toLowerCase().localeCompare(b.title.toLowerCase()))
  }

  return list
})

// ── UI state ──
const drawerOpen      = ref(false)
const showPreview     = ref(false)
const filterSheetOpen = ref(false)
const saving          = ref(false)
const showDeleteModal = ref(false)
const deletingId      = ref(null)
const deleting        = ref(false)
const titleInput      = ref(null)

// ── Form ──
const BLANK_DETAILS = () => ({
  options: [''], min: '', max: '', default: '', min_date: '', max_date: '',
  scale: '5', max_size_mb: '5', allowed_types: [],
  placeholder: '', min_length: '', max_length: '',
  declaration_text: '',
  date_mode: 'date', min_time: '', max_time: '',
})

const form = reactive({
  id: null,
  title: '',
  question_type: 'one_line',
  state: 'active',
  question_details: BLANK_DETAILS(),
})
const formErrors = ref({})

const isChoiceType      = computed(() => CHOICE_TYPES.includes(form.question_type))
const isTextInputType   = computed(() => ['one_line', 'multi_line'].includes(form.question_type))
const isPlaceholderType = computed(() => ['one_line', 'multi_line', 'phone_number', 'email', 'number', 'url'].includes(form.question_type))
const isNumberType      = computed(() => form.question_type === 'number')
const isDateType        = computed(() => form.question_type === 'date')
const isHiddenType      = computed(() => form.question_type === 'hidden')
const isRatingType      = computed(() => form.question_type === 'rating')
const isFileUploadType  = computed(() => form.question_type === 'file_upload')
const isSignatureType    = computed(() => form.question_type === 'signature')
const isDefaultValueType = computed(() => !['rating', 'file_upload', 'signature'].includes(form.question_type))

// Converts YYYY-MM-DD strings to local Date objects so v-calendar renders correctly,
// and writes back as YYYY-MM-DD strings when the user picks from the calendar.
function strToLocalDate(str) {
  if (!str) return null
  const [y, m, d] = str.split('-').map(Number)
  return new Date(y, m - 1, d)
}
function dateToStr(date) {
  if (!date) return ''
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-')
}
const dateRangeModel = computed({
  get() {
    return {
      start: strToLocalDate(form.question_details.min_date),
      end:   strToLocalDate(form.question_details.max_date),
    }
  },
  set(val) {
    form.question_details.min_date = dateToStr(val?.start)
    form.question_details.max_date = dateToStr(val?.end)
  },
})

// Writable computed for the default value when date_mode is 'date_range'
const defaultDateRangeModel = computed({
  get() {
    const str = form.question_details.default || ''
    const parts = str.split(',', 2)
    return {
      start: strToLocalDate(parts[0]?.trim() || ''),
      end:   strToLocalDate(parts[1]?.trim() || ''),
    }
  },
  set(val) {
    form.question_details.default = `${dateToStr(val?.start)},${dateToStr(val?.end)}`
  },
})

// ── Option input refs (reset each render cycle so stale refs don't linger) ──
const optionRefs = []
onBeforeUpdate(() => { optionRefs.length = 0 })

function focusOptionInput() {
  nextTick(() => {
    const allEmpty = form.question_details.options.every(o => o.trim() === '')
    const idx = allEmpty ? 0 : form.question_details.options.length - 1
    optionRefs[idx]?.focus()
  })
}

// Auto-focus options panel when a choice type is selected
watch(() => form.question_type, (type) => {
  if (CHOICE_TYPES.includes(type)) focusOptionInput()
})

// ── Drag-to-reorder ──
const dragIndex     = ref(null)
const dragOverIndex = ref(null)

function onDragStart(e, idx) {
  dragIndex.value = idx
  e.dataTransfer.effectAllowed = 'move'
}
function onDragOver(e, idx) {
  e.dataTransfer.dropEffect = 'move'
  dragOverIndex.value = idx
}
function onDragLeave() { dragOverIndex.value = null }
function onDrop(e, idx) {
  if (dragIndex.value === null || dragIndex.value === idx) {
    dragIndex.value = null
    dragOverIndex.value = null
    return
  }
  const opts = form.question_details.options
  const moved = opts.splice(dragIndex.value, 1)[0]
  opts.splice(idx, 0, moved)
  dragIndex.value = null
  dragOverIndex.value = null
}
function onDragEnd() {
  dragIndex.value = null
  dragOverIndex.value = null
}

// ── Options helpers ──
function addOption() {
  const emptyIdx = form.question_details.options.findIndex(o => o.trim() === '')
  if (emptyIdx !== -1) {
    nextTick(() => optionRefs[emptyIdx]?.focus())
    return
  }
  form.question_details.options.push('')
  nextTick(() => {
    optionRefs[form.question_details.options.length - 1]?.focus()
  })
}
function removeOption(idx) {
  form.question_details.options.splice(idx, 1)
}

// ── Lifecycle ──
onMounted(() => {
  store.fetchIfStale()
  document.addEventListener('keydown', handleEsc)
})
onUnmounted(() => document.removeEventListener('keydown', handleEsc))
function handleEsc(e) { if (e.key === 'Escape') closeDrawer() }

async function retry() {
  store.invalidate()
  await store.fetchIfStale()
}

// ── Drawer ──
function resetForm() {
  Object.assign(form, { id: null, title: '', question_type: 'one_line', state: 'active', question_details: BLANK_DETAILS() })
  formErrors.value = {}
}

function openCreate() {
  resetForm()
  drawerOpen.value = true
  nextTick(() => titleInput.value?.focus())
}

function openEdit(question) {
  const qd = question.question_details ?? {}
  Object.assign(form, {
    id: question.id,
    title: question.title,
    question_type: question.question_type,
    state: question.state,
    question_details: {
      options:       qd.options?.length ? [...qd.options] : [''],
      min:           qd.min          ?? '',
      max:           qd.max          ?? '',
      default:       qd.default      ?? '',
      min_date:      qd.min_date     ?? '',
      max_date:      qd.max_date     ?? '',
      scale:         String(qd.scale  ?? '5'),
      max_size_mb:   qd.max_size_mb  ?? '5',
      allowed_types:    qd.allowed_types ? [...qd.allowed_types] : [],
      placeholder:      qd.placeholder      ?? '',
      min_length:       qd.min_length       ?? '',
      max_length:       qd.max_length       ?? '',
      declaration_text: qd.declaration_text ?? '',
      date_mode:        qd.date_mode        ?? 'date',
      min_time:         qd.min_time         ?? '',
      max_time:         qd.max_time         ?? '',
    },
  })
  formErrors.value = {}
  drawerOpen.value = true
  nextTick(() => titleInput.value?.focus())
}

function closeDrawer() { drawerOpen.value = false }

// ── Form validation ──
function validateForm() {
  const errs = {}
  if (!form.title.trim()) errs.title = "Question title can't be blank"
  if (isChoiceType.value && !form.question_details.options.some(o => o.trim())) {
    errs.options = 'At least one option is required'
  }
  formErrors.value = errs
  return Object.keys(errs).length === 0
}

function buildQuestionDetails() {
  // Helper: include default only if this type supports it and a value is set
  const defaultVal = isDefaultValueType.value && form.question_details.default?.trim()
    ? form.question_details.default.trim()
    : undefined

  if (isChoiceType.value) {
    const d = { options: form.question_details.options.filter(o => o.trim()) }
    if (defaultVal) d.default = defaultVal
    return d
  }
  if (isTextInputType.value) {
    const d = {}
    if (form.question_details.placeholder?.trim()) d.placeholder = form.question_details.placeholder.trim()
    if (form.question_details.min_length !== '')   d.min_length  = form.question_details.min_length
    if (form.question_details.max_length !== '')   d.max_length  = form.question_details.max_length
    if (defaultVal) d.default = defaultVal
    return d
  }
  if (isNumberType.value) {
    const d = {}
    if (form.question_details.placeholder?.trim()) d.placeholder = form.question_details.placeholder.trim()
    if (form.question_details.min !== '') d.min = form.question_details.min
    if (form.question_details.max !== '') d.max = form.question_details.max
    if (defaultVal) d.default = defaultVal
    return d
  }
  if (isDateType.value) {
    const mode = form.question_details.date_mode || 'date'
    const d = { date_mode: mode }
    if (['date', 'date_range'].includes(mode)) {
      if (form.question_details.min_date) d.min_date = form.question_details.min_date
      if (form.question_details.max_date) d.max_date = form.question_details.max_date
    }
    if (['time', 'datetime'].includes(mode)) {
      if (form.question_details.min_time) d.min_time = form.question_details.min_time
      if (form.question_details.max_time) d.max_time = form.question_details.max_time
    }
    if (defaultVal) d.default = defaultVal
    return d
  }
  if (isHiddenType.value) {
    // Hidden always sends default (even empty) so URL overrides work
    return { default: form.question_details.default }
  }
  if (isRatingType.value) {
    return { scale: form.question_details.scale || '5' }
  }
  if (isFileUploadType.value) {
    const d = {}
    if (form.question_details.max_size_mb !== '') d.max_size_mb = form.question_details.max_size_mb
    if (form.question_details.allowed_types?.length) d.allowed_types = [...form.question_details.allowed_types]
    return d
  }
  if (isSignatureType.value) {
    const d = {}
    if (form.question_details.declaration_text?.trim()) d.declaration_text = form.question_details.declaration_text.trim()
    return d
  }
  // phone_number, email, url, boolean
  if (isPlaceholderType.value) {
    const d = {}
    if (form.question_details.placeholder?.trim()) d.placeholder = form.question_details.placeholder.trim()
    if (defaultVal) d.default = defaultVal
    return d
  }
  // boolean (not in PLACEHOLDER_TYPES) and any remaining types
  const d = {}
  if (defaultVal) d.default = defaultVal
  return d
}

async function submitForm() {
  if (!validateForm()) return
  saving.value = true

  const payload = {
    question: {
      title: form.title,
      question_type: form.question_type,
      state: form.state,
      question_details: buildQuestionDetails(),
    },
  }

  const res = form.id
    ? await axiosRequest('PUT', `/api/v1/host/questions/${form.id}`, payload, false)
    : await axiosRequest('POST', '/api/v1/host/questions', payload, false)

  saving.value = false

  if (!res.js_error && res.response.success) {
    flash.value?.show(res.response.message, 'notice')
    if (form.id) {
      store.updateQuestion(form.id, res.response.data)
    } else {
      store.addQuestion(res.response.data)
    }
    closeDrawer()
  } else {
    flash.value?.show(res.error?.data?.message || 'Failed to save question.', 'alert')
  }
}

// ── Delete ──
function openDelete(id) { deletingId.value = id; showDeleteModal.value = true }

async function deleteQuestion() {
  if (!deletingId.value) return
  deleting.value = true
  const res = await axiosRequest('DELETE', `/api/v1/host/questions/${deletingId.value}`, {}, false)
  deleting.value = false
  showDeleteModal.value = false
  if (!res.js_error && res.response.success) {
    flash.value?.show('Question deleted', 'notice')
    store.removeQuestion(deletingId.value)
    deletingId.value = null
  } else {
    flash.value?.show('Failed to delete question.', 'alert')
  }
}
</script>

<style scoped>
/* Toolbar selects: hide native arrow and inject a consistent SVG chevron via background-image.
   This is the only cross-browser reliable approach (works in Chrome, Firefox, Safari, Edge). */
select.toolbar-select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke-width='2' stroke='%239ca3af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='m19 9-7 7-7-7'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.45rem center;
  background-size: 0.9rem 0.9rem;
}

/* Drawer slide-in from right */
.drawer-enter-active { transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1); }
.drawer-leave-active { transition: transform 0.22s cubic-bezier(0.4, 0, 1, 1); }
.drawer-enter-from,
.drawer-leave-to    { transform: translateX(100%); }

/* Backdrop */
.backdrop-enter-active { transition: opacity 0.25s ease; }
.backdrop-leave-active { transition: opacity 0.2s ease; }
.backdrop-enter-from,
.backdrop-leave-to    { opacity: 0; }

/* Bottom sheet slide up */
.sheet-enter-active { transition: transform 0.32s cubic-bezier(0.32, 0.72, 0, 1); }
.sheet-leave-active { transition: transform 0.25s cubic-bezier(0.32, 0.72, 0, 1); }
.sheet-enter-from,
.sheet-leave-to     { transform: translateY(100%); }

/* Modal pop */
.modal-pop-enter-active { transition: opacity 0.2s ease, transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1); }
.modal-pop-leave-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.modal-pop-enter-from,
.modal-pop-leave-to     { opacity: 0; transform: scale(0.95); }

/* Options reveal */
.options-reveal-enter-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.options-reveal-enter-from   { opacity: 0; transform: translateY(-8px); }
.options-reveal-leave-active { transition: opacity 0.15s ease; }
.options-reveal-leave-to     { opacity: 0; }

/* Delete modal */
.modal-enter-active { transition: opacity 0.2s ease; }
.modal-leave-active { transition: opacity 0.15s ease; }
.modal-enter-from,
.modal-leave-to     { opacity: 0; }
.modal-enter-active .modal-card { transition: transform 0.2s ease; }
.modal-enter-from  .modal-card  { transform: scale(0.96) translateY(8px); }
</style>
